import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  MessageSquare,
  CheckSquare,
  PenTool,
  Target,
  Gamepad2,
  Cloud,
  Eye,
  Award,
  Calendar,
  TrendingUp,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../utils/supabase/client';
import { getStudentSession } from '../../utils/supabase/auth';
import { getLesson } from '../../utils/supabase/queries';

interface ActivitySubmission {
  id: string;
  slideType: string;
  name: string;
  icon: any;
  score: number | null;
  maxScore: number | null;
  percentage: number | null;
  status: 'auto-graded' | 'teacher-graded' | 'pending';
  submittedAt: Date;
  teacherComment?: string;
  timeSpent: number | null;
}

const autoGradedTypes = new Set([
  'multiple_choice',
  'quiz',
  'true_false',
  'matching_pairs',
  'time_to_climb',
  'fill_blanks',
]);

const activityIconMap: Record<string, any> = {
  multiple_choice: CheckSquare,
  quiz: CheckSquare,
  true_false: CheckCircle2,
  poll: Target,
  open_ended: MessageSquare,
  matching_pairs: Target,
  draw_it: PenTool,
  time_to_climb: Gamepad2,
  word_cloud: Cloud,
  drag_drop: Eye,
  sequencing: Award,
  label_diagram: Eye,
  collaborate_board: MessageSquare,
  fill_blanks: CheckSquare,
};

export default function StudentLessonDetail() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState<any>(null);
  const [activities, setActivities] = useState<ActivitySubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (lessonId) {
      loadLessonDetails();
    }
  }, [lessonId]);

  const loadLessonDetails = async () => {
    try {
      setLoading(true);
      const studentSession = getStudentSession();
      if (!studentSession) {
        setError('Please join a session first');
        return;
      }

      // Get lesson details
      if (lessonId) {
        const lessonData = await getLesson(lessonId);

        const { data: gradeRows, error: gradeError } = await supabase
          .from('grades')
          .select('id, total_score, max_possible_score, completion_percentage, updated_at')
          .eq('lesson_id', lessonId)
          .eq('student_name', studentSession.studentName)
          .order('updated_at', { ascending: false })
          .limit(1);

        if (gradeError) throw gradeError;

        const grade = gradeRows?.[0];
        const overallScore = grade?.max_possible_score
          ? Math.round((grade.total_score / grade.max_possible_score) * 100)
          : 0;

        setLesson({
          id: lessonData.id,
          title: lessonData.title,
          subject: null,
          mode: lessonData.mode,
          overallScore,
          completion: grade?.completion_percentage ?? 0,
          completedAt: grade?.updated_at ? new Date(grade.updated_at) : undefined,
        });

        const { data: submissionsData, error: submissionsError } = await supabase
          .from('submissions')
          .select(`
            id,
            slide_id,
            answer,
            score,
            graded,
            created_at,
            slides (
              id,
              type,
              content,
              lesson_id
            )
          `)
          .eq('student_name', studentSession.studentName)
          .order('created_at', { ascending: true });

        if (submissionsError) throw submissionsError;

        const lessonSubmissions = (submissionsData || []).filter((sub: any) => {
          return sub.slides?.lesson_id === lessonId;
        });

        if (lessonSubmissions.length > 0) {
          const activitiesData = lessonSubmissions.map((sub: any, idx: number) => {
            const slide = sub.slides || {};
            const content = slide.content || {};
            const slideType = slide.type || 'activity';
            const maxScore =
              typeof content.points === 'number'
                ? content.points
                : typeof content.maxPoints === 'number'
                ? content.maxPoints
                : null;
            const percentage =
              sub.score !== null && maxScore
                ? Math.round((sub.score / Math.max(maxScore, 1)) * 100)
                : null;

            return {
              id: sub.id,
              slideType,
              type: 'quiz' as const,
              name:
                content.title ||
                content.question ||
                content.prompt ||
                content.instructions ||
                `Activity ${idx + 1}`,
              icon: activityIconMap[slideType] || CheckSquare,
              score: sub.score ?? null,
              maxScore,
              percentage,
              status: sub.graded
                ? autoGradedTypes.has(slideType)
                  ? 'auto-graded'
                  : 'teacher-graded'
                : 'pending',
              submittedAt: new Date(sub.created_at),
              teacherComment: sub.answer?.feedback,
              timeSpent: typeof sub.answer?.timeSpent === 'number' ? sub.answer.timeSpent : null,
            };
          });

          setActivities(activitiesData);
        } else {
          setActivities([]);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading lesson details:', err);
      setError('Failed to load lesson details');
      toast.error('Failed to load lesson details');
      setLoading(false);
    }
  };

  const getScoreColor = (percentage: number) => {
    if (percentage >= 90) return 'text-green-600 bg-green-100 border-green-200';
    if (percentage >= 80) return 'text-emerald-600 bg-emerald-100 border-emerald-200';
    if (percentage >= 70) return 'text-orange-600 bg-orange-100 border-orange-200';
    if (percentage >= 60) return 'text-amber-600 bg-amber-100 border-amber-200';
    return 'text-red-600 bg-red-100 border-red-200';
  };

  const getStatusBadge = (status: ActivitySubmission['status']) => {
    switch (status) {
      case 'auto-graded':
        return (
          <Badge className="bg-blue-100 text-blue-700 border-blue-200 rounded-lg text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Auto-graded
          </Badge>
        );
      case 'teacher-graded':
        return (
          <Badge className="bg-purple-100 text-purple-700 border-purple-200 rounded-lg text-xs">
            <MessageSquare className="w-3 h-3 mr-1" />
            Teacher-graded
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 rounded-lg text-xs">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  const gradedActivities = activities.filter(a => a.percentage !== null);
  const averageScore = gradedActivities.length > 0
    ? Math.round(gradedActivities.reduce((sum, a) => sum + (a.percentage || 0), 0) / gradedActivities.length)
    : 0;

  const totalTimeSeconds = activities.reduce((sum, activity) => {
    if (typeof activity.timeSpent === 'number') {
      return sum + activity.timeSpent;
    }
    return sum;
  }, 0);

  return (
    <div className="min-h-screen bg-background">
      {loading && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading lesson details...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{error}</h2>
            <Button onClick={() => navigate('/student/grades')}>
              Back to Grades
            </Button>
          </div>
        </div>
      )}

      {!loading && !error && lesson && (
        <>
          {/* Header */}
          <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/student/grades')}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Grades
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Lesson Header */}
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-border rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
            <div className="flex-1 w-full">
              {lesson.subject && (
                <Badge className="mb-2 sm:mb-3 rounded-lg text-xs sm:text-sm">{lesson.subject}</Badge>
              )}
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">{lesson.title}</h1>
              <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground flex-wrap">
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  {lesson.completedAt ? `Completed ${lesson.completedAt.toLocaleDateString()}` : 'Not completed yet'}
                </div>
                <span>•</span>
                <Badge
                  className={`rounded-lg text-xs sm:text-sm ${
                    lesson.mode === 'live'
                      ? 'bg-red-100 text-red-700 border-red-200'
                      : 'bg-blue-100 text-blue-700 border-blue-200'
                  }`}
                >
                  {lesson.mode === 'live' ? 'Live Session' : 'Student-Paced'}
                </Badge>
              </div>
            </div>

            <div className="text-right w-full lg:w-auto">
              <p className="text-xs sm:text-sm text-muted-foreground mb-1">Overall Score</p>
              <p className={`text-4xl sm:text-5xl font-bold ${
                lesson.overallScore >= 90 ? 'text-green-600' :
                lesson.overallScore >= 80 ? 'text-emerald-600' :
                lesson.overallScore >= 70 ? 'text-orange-600' :
                'text-red-600'
              }`}>
                {lesson.overallScore}%
              </p>
            </div>
          </div>

          {/* Completion Bar */}
          <div className="mt-4 sm:mt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm font-medium text-foreground">Completion</p>
              <p className="text-xs sm:text-sm font-semibold text-foreground">{lesson.completion}%</p>
            </div>
            <Progress value={lesson.completion} className="h-3" />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Average Score</p>
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-foreground">{averageScore}%</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Activities</p>
              <Award className="w-4 h-4 sm:w-5 sm:h-5 text-accent" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-foreground">
              {activities.length}
            </p>
          </div>

          <div className="bg-card border border-border rounded-xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs sm:text-sm text-muted-foreground">Total Time</p>
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-foreground">
              {totalTimeSeconds > 0 ? `${Math.floor(totalTimeSeconds / 60)}m` : '--'}
            </p>
          </div>
        </div>

        {/* Activities List */}
        <div className="bg-card border border-border rounded-2xl p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-4 sm:mb-6">Activity Breakdown</h2>
          
          <div className="space-y-3 sm:space-y-4">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className="border border-border rounded-xl p-3 sm:p-6 hover:shadow-md transition-all"
              >
                <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-3 sm:gap-4">
                  <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <activity.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-semibold text-sm sm:text-base">{activity.name}</h3>
                        {getStatusBadge(activity.status)}
                      </div>
                      
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground mb-2 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                          {typeof activity.timeSpent === 'number'
                            ? `${Math.floor(activity.timeSpent / 60)}m ${activity.timeSpent % 60}s`
                            : 'Time not available'}
                        </div>
                        <span>•</span>
                        <span>{activity.submittedAt.toLocaleString()}</span>
                      </div>

                      {activity.teacherComment && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mt-2 sm:mt-3">
                          <p className="text-xs sm:text-sm font-semibold text-blue-900 mb-1 flex items-center gap-1">
                            <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4" />
                            Teacher Feedback
                          </p>
                          <p className="text-xs sm:text-sm text-blue-800">{activity.teacherComment}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 w-full sm:w-auto">
                    {activity.percentage !== null ? (
                      <div className="text-right">
                        <div className={`inline-flex flex-col items-center gap-1 px-3 sm:px-4 py-1 sm:py-2 rounded-xl border-2 font-bold text-sm sm:text-base ${getScoreColor(activity.percentage)}`}>
                          <span>{activity.score}</span>
                          <span className="text-xs opacity-75">/ {activity.maxScore}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{activity.percentage}%</p>
                      </div>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-xs">
                        Not Graded
                      </Badge>
                    )}

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/student/activity/${activity.id}`)}
                      className="rounded-lg text-xs sm:text-sm"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Details</span>
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Grade Weighting Info */}
        <div className="mt-6 sm:mt-8 bg-card border border-border rounded-2xl p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 sm:mb-4">Grade Calculation</h3>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Quizzes & Matching</span>
              <span className="font-semibold">30%</span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Open-Ended Questions</span>
              <span className="font-semibold">25%</span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Draw-It Activities</span>
              <span className="font-semibold">20%</span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Interactive Games</span>
              <span className="font-semibold">15%</span>
            </div>
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Participation</span>
              <span className="font-semibold">10%</span>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}
